"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowLeftBold, PiArrowRightBold, PiArrowRight } from "react-icons/pi";

interface PortfolioItem {
  title: string;
  category: string;
  image: string;
  url?: string;
}

export default function OvanPortfolio({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [startIndex, setStartIndex] = useState(0);
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");

      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };

      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => {
        window.removeEventListener("ovan-theme-change", handleThemeChange);
      };
    }
  }, []);

  const defaultItems: PortfolioItem[] = [
    { title: "Brand Identity Campaign", category: "Motion Graphics", image: "/assets/1-KdbN7dlr.jpg" },
    { title: "Typeface & Texture", category: "Motion Graphics", image: "/assets/2-D66jnrwj.jpg" },
    { title: "Sculpted Silhouettes", category: "Motion Graphics", image: "/assets/3-Cr_w0-_u.jpg" },
    { title: "Crafted Typography", category: "Motion Graphics", image: "/assets/4-BS1kc8WN.jpg" },
    { title: "Fluid Movement", category: "Motion Graphics", image: "/assets/5-B1QFU0hW.jpg" },
    { title: "Dynamic Storytelling", category: "Motion Graphics", image: "/assets/6-6jmjvmjj.jpg" },
  ];

  const items: PortfolioItem[] = content.items || defaultItems;

  const handleNext = () => {
    if (startIndex + 3 < items.length) {
      setStartIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (startIndex > 0) {
      setStartIndex(prev => prev - 1);
    }
  };

  const updateItem = (idx: number, field: keyof PortfolioItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  const updateBullet = (idx: number, val: string) => {
    if (!onContentChange) return;
    const newBullets = [...(content.bulletPoints || [])];
    newBullets[idx] = val;
    onContentChange({ ...content, bulletPoints: newBullets });
  };

  return (
    <section
      id="services"
      className={`relative py-16 md:py-24 transition-colors duration-200 ${
        isDark ? "bg-slate-800" : "bg-slate-50"
      } ${
        isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Slider Navigation Controls */}
        <div className="grid md:grid-cols-12 grid-cols-1 pb-10 items-end gap-6">
          <div className="lg:col-span-8 md:col-span-8 text-center md:text-start">
            <h3 className={`mb-4 text-2xl md:text-3xl leading-normal font-semibold transition-colors ${
              isDark ? "text-white" : "text-slate-900"
            }`}>
              <EditableText
                value={content.title || "Our Digital Product"}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, title: val })
                }
                as="span"
                className={isDark ? "text-white" : "text-slate-900"}
                placeholder="Portfolio Title"
              />
            </h3>
            <p className="text-slate-400 max-w-xl">
              <EditableText
                value={
                  content.text ||
                  "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"
                }
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, text: val })
                }
                as="span"
                placeholder="Portfolio Description"
              />
            </p>

            {/* Bullet Points */}
            {content.bulletPoints && content.bulletPoints.length > 0 && (
              <ul className={`list-none space-y-3 my-6 text-start transition-colors ${
                isDark ? "text-slate-300" : "text-slate-500"
              }`}>
                {content.bulletPoints.map((bullet: string, idx: number) => (
                  <li key={idx} className="flex items-center">
                    <PiArrowRight className="text-blue-600 me-3 size-4 flex-shrink-0" />
                    <span>
                      <EditableText
                        value={bullet}
                        onChange={(val) => updateBullet(idx, val)}
                        as="span"
                        placeholder="Bullet Point"
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Read More button */}
            {!content.hideReadMore && (
              <div className="mt-6 text-start">
                <a
                  href={content.readMoreLink || "#"}
                  className="h-10 px-5 tracking-wider inline-flex items-center text-xs uppercase justify-center font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-md"
                >
                  <EditableText
                    value={content.btnText || "Read More"}
                    onChange={(val) =>
                      onContentChange && onContentChange({ ...content, btnText: val })
                    }
                    as="span"
                    placeholder="CTA Text"
                  />
                  <PiArrowRight className="ms-1 size-3.5" />
                </a>
              </div>
            )}
          </div>
          
          <div className="lg:col-span-4 md:col-span-4 flex justify-center md:justify-end gap-3">
            <button
              onClick={handlePrev}
              disabled={startIndex === 0}
              className={`size-10 rounded-full flex items-center justify-center border disabled:opacity-40 transition-all ${
                isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              <PiArrowLeftBold className="size-4" />
            </button>
            <button
              onClick={handleNext}
              disabled={startIndex + 3 >= items.length}
              className={`size-10 rounded-full flex items-center justify-center border disabled:opacity-40 transition-all ${
                isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              <PiArrowRightBold className="size-4" />
            </button>
          </div>
        </div>

        {/* Carousel Slider */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 overflow-hidden">
          {items.slice(startIndex, startIndex + 3).map((item, idx) => (
            <a
              key={idx}
              href={item.url || "#"}
              className={`group relative overflow-hidden rounded-xl shadow-md transition-all duration-300 border block ${
                isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"
              }`}
            >
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-b to-slate-950 via-slate-900/40 from-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <span className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">
                  {item.category}
                </span>
                <span className="text-white font-bold text-lg mb-2">
                  {item.title}
                </span>
                <span className="inline-flex items-center text-sm font-semibold text-blue-400 hover:text-blue-300">
                  View Project →
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
